import { useCallback, useEffect } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import {
  Flex,
  HeaderIcon,
  IconArrowRight,
  IconClose,
  IconInfo,
  IconLogoApple,
  IconLogoKakao,
  IconSigninEmail,
  IconSigninFacebook,
  IconSigninNaver,
  IconUnlock,
  StackScreen,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { useAppDownloadModal, useHandleBackPressed } from 'shared/src/hooks';
import { isWeb, useNavigator } from 'shared/src/utils';
import { useAlreadyRegistered } from '../../socialAuthHandoff';
import { useLoginPress } from '../Login/hooks/useLoginPress';
import type { LoginType } from '../Login/types';

/** verify 응답 channel(대문자) → 재로그인에 쓸 LoginType */
const CHANNEL_TO_LOGIN_TYPE: Record<string, LoginType> = {
  EMAIL: 'email',
  KAKAO: 'kakao',
  NAVER: 'naver',
  FACEBOOK: 'facebook',
  APPLE: 'apple',
};

/** email 표시가 없을 때 계정 라벨로 쓸 채널 표시명 */
const CHANNEL_LABEL: Record<string, string> = {
  EMAIL: '이메일',
  KAKAO: '카카오',
  NAVER: '네이버',
  FACEBOOK: '페이스북',
  APPLE: '애플',
};

const ChannelIcon = ({ channel }: { channel: string }) => {
  switch (channel) {
    case 'EMAIL':
      return <IconSigninEmail size={24} />;
    case 'NAVER':
      return <IconSigninNaver size={24} />;
    case 'FACEBOOK':
      return <IconSigninFacebook size={24} />;
    case 'KAKAO':
      return <IconLogoKakao size={24} />;
    case 'APPLE':
      return <IconLogoApple size={24} fill={'#000000'} />;
    default:
      return null;
  }
};

type TypographyType = ComponentProps<typeof Typography>['type'];
type TypographyColor = ComponentProps<typeof Typography>['color'];

const InfoRow = ({
  icon,
  label,
  labelType = 'body2',
  labelColor = 'content_secondary',
  showArrow = false,
  onPress,
}: {
  icon: ReactNode;
  label: string;
  labelType?: TypographyType;
  labelColor?: TypographyColor;
  showArrow?: boolean;
  onPress: () => void;
}) => (
  <Flex
    horizontal
    alignItems={'center'}
    justifyContent={'space-between'}
    gap={16}
    borderWidth={1}
    borderColor={'border_secondary'}
    borderRadius={4}
    padding={{ vertical: 13, horizontal: 12 }}
    onPress={onPress}
  >
    <Flex horizontal alignItems={'center'} gap={16}>
      {icon}
      <Typography type={labelType} color={labelColor}>
        {label}
      </Typography>
    </Flex>
    {showArrow && <IconArrowRight size={20} />}
  </Flex>
);

/**
 * 기가입 채널 안내 화면 (웹 전용).
 * 소셜 로그인 시 다른 채널로 이미 가입된 계정이 감지되면(게이트) 이 화면으로 와서
 * 기존 채널로 재로그인하도록 안내한다. channel/email/hasMultiLoginType은 아톰으로
 * 전달받고, 재로그인 dispatch는 useLoginPress를 재사용한다(원래 redirect는 URL에 유지됨).
 */
export const AlreadyRegisteredScreen = () => {
  const { width } = useScreenSize();
  const { onPressBack } = useHandleBackPressed();
  const { navigator } = useNavigator();
  const handlePress = useLoginPress();
  const { show: showAppDownloadModal } = useAppDownloadModal();
  const [info, setInfo] = useAlreadyRegistered();

  // 새로고침·직접진입 등으로 컨텍스트(아톰)가 유실되면 막다른 화면 대신 로그인으로 복귀한다.
  // 언마운트 시엔 PII(email)가 전역 store에 남지 않도록 아톰을 비운다.
  useEffect(() => {
    if (isWeb && info == null) {
      const redirect =
        new URLSearchParams(window.location.search).get('redirect') ??
        undefined;
      navigator.moveToLogin(redirect);
    }
    return () => setInfo(null);
  }, [info, navigator, setInfo]);

  const channel = info?.channel ?? '';
  const email = info?.email ?? '';
  const hasMultiLoginType = info?.hasMultiLoginType ?? false;
  const isEmailChannel = channel === 'EMAIL';
  const loginType = CHANNEL_TO_LOGIN_TYPE[channel];
  // email(표시용)이 없어도 재로그인은 가능하므로 채널명으로 대체 표시한다(애플 hide-my-email 등).
  const accountLabel =
    email !== '' ? email : `${CHANNEL_LABEL[channel] ?? ''} 계정`;

  // 다중 로그인은 앱 설치 안내, 이메일 채널은 비밀번호 찾기, 그 외는 소셜 도움말.
  const helpText = hasMultiLoginType
    ? '앱 설치하고 쉽게 로그인하기'
    : isEmailChannel
      ? '비밀번호 찾기'
      : '소셜 계정으로 로그인 되지 않나요?';
  const HelpIcon = hasMultiLoginType || isEmailChannel ? IconUnlock : IconInfo;

  const handlePressHelp = useCallback(() => {
    // 이메일 채널은 비밀번호 찾기, 그 외(소셜)는 앱 설치 안내.
    if (isEmailChannel) {
      navigator.moveToRecoveryPassword();
      return;
    }
    showAppDownloadModal();
  }, [isEmailChannel, navigator, showAppDownloadModal]);

  return (
    <StackScreen
      hideBackButton
      rightIcons={[
        <HeaderIcon key={'close'} onPress={onPressBack}>
          <IconClose />
        </HeaderIcon>,
      ]}
    >
      <Flex flex={1} width={width} alignSelf={'center'}>
        <Flex padding={{ horizontal: 16, top: 24 }} gap={8}>
          <Typography type={'h5'} color={'content_primary'}>
            {`이미 가입된 계정이 있어요${
              hasMultiLoginType ? '\n어떤 방법으로 로그인할까요?' : ''
            }`}
          </Typography>
          <Typography
            type={'body2'}
            color={hasMultiLoginType ? 'content_blue_primary' : 'content_tertiary'}
          >
            {hasMultiLoginType
              ? '내 쿠폰과 포인트는 그대로 유지돼요'
              : '하나의 계정으로 에이블리, 4910 모두 이용 가능합니다'}
          </Typography>
        </Flex>

        {loginType != null && (
          <Flex padding={{ horizontal: 16, top: 8 }} gap={8}>
            {/* 소셜 채널이면서 다중 로그인이면 이메일 계정으로도 로그인할 수 있음을 함께 안내. */}
            {!isEmailChannel && hasMultiLoginType && email !== '' && (
              <InfoRow
                icon={<IconSigninEmail size={24} />}
                label={email}
                showArrow
                onPress={() => handlePress('email')}
              />
            )}
            <InfoRow
              icon={<ChannelIcon channel={channel} />}
              label={accountLabel}
              showArrow={!hasMultiLoginType}
              onPress={() => handlePress(loginType)}
            />
            <InfoRow
              icon={<HelpIcon size={24} />}
              label={helpText}
              labelType={'subtitle1.5'}
              labelColor={'content_primary'}
              onPress={handlePressHelp}
            />
          </Flex>
        )}
      </Flex>
    </StackScreen>
  );
};
